import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function getInitialClientIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("client_id") || params.get("clientId") || "";
  } catch {
    return "";
  }
}

function isTeamRole(role) {
  return ["admin", "team"].includes(role);
}

function canImportForClient(role, plan) {
  return (
    role === "expert" ||
    role === "family_office" ||
    isTeamRole(role) ||
    plan === "art_advisor" ||
    plan === "art_advisor_premium" ||
    plan === "family_office"
  );
}

export default function ImportArtprice() {
  const { t, i18n } = useTranslation();
  const tr = useCallback((key, options) => t(`artpriceImport.${key}`, options), [t]);
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(getInitialClientIdFromUrl());
  const [manualClientId, setManualClientId] = useState("");

  const [q, setQ] = useState("");
  const [houses, setHouses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const [addName, setAddName] = useState("");
  const [addCity, setAddCity] = useState("");
  const [addCountry, setAddCountry] = useState("");
  const [addWebsite, setAddWebsite] = useState("");
  const [addAliases, setAddAliases] = useState("");
  const [savingAdd, setSavingAdd] = useState(false);
  const [importing, setImporting] = useState(false);

  const listRef = useRef(null);
  const urlParams = new URLSearchParams(window.location.search);
  const artworkIdFromUrl = urlParams.get("artworkId") || "";
  const artistFromUrl = urlParams.get("artist") || "";
  
  const role = profile?.role || "user";
  const plan = profile?.plan || "standard";
  const canChooseClient = canImportForClient(role, plan);

  const effectiveClientId = useMemo(() => {
    if (canChooseClient) return selectedClientId || manualClientId || "";
    return session?.user?.id || "";
  }, [canChooseClient, selectedClientId, manualClientId, session?.user?.id]);

  const importContext = useMemo(() => {
    const userId = session?.user?.id || "";
    const targetClientId = effectiveClientId || (role === "user" ? userId : "");
    const isForAnotherClient = Boolean(targetClientId && targetClientId !== userId);

    return {
      owner_id: targetClientId || userId,
      client_id: targetClientId || null,
      expert_id: role === "expert" ? userId : null,
      created_by: userId,
      uploaded_by: userId,
      role_uploaded: role,
      visibility: isTeamRole(role)
        ? "team_artkiv"
        : isForAnotherClient
          ? "expert_client"
          : "private",
    };
  }, [effectiveClientId, role, session?.user?.id]);

  useEffect(() => {
    let isCancelled = false;

    async function bootstrap() {
      const { data, error } = await supabase.auth.getSession();
      if (error || !data?.session) {
        setMsg(`❌ ${tr("messages.loginRequired")}`);
        return;
      }

      if (isCancelled) return;
      setSession(data.session);

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id,role,plan,first_name,last_name,company,company_name")
        .eq("id", data.session.user.id)
        .single();

      if (!isCancelled) {
        if (profileError) console.error("Erreur profil import Artprice:", profileError);
        setProfile(profileData || { id: data.session.user.id, role: "user", plan: "standard" });
      }
    }

    bootstrap();
    return () => {
      isCancelled = true;
    };
  }, [tr]);

  useEffect(() => {
    let isCancelled = false;

    async function loadAccessibleClients() {
      if (!session?.user?.id || !canChooseClient) return;

      const { data: perms, error: permsError } = await supabase
        .from("collection_permissions")
        .select("collection_id")
        .eq("grantee_id", session.user.id);

      if (permsError) {
        console.error("Erreur chargement permissions collections:", permsError);
        return;
      }

      const collectionIds = [...new Set((perms || []).map((p) => p.collection_id).filter(Boolean))];
      if (!collectionIds.length) return;

      const { data: works, error: worksError } = await supabase
        .from("artworks")
        .select("client_id")
        .in("collection_id", collectionIds);

      if (worksError) {
        console.error("Erreur chargement clients accessibles:", worksError);
        return;
      }

      const clientIds = [...new Set((works || []).map((w) => w.client_id).filter(Boolean))];
      if (!clientIds.length) return;

      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id,first_name,last_name,company,company_name")
        .in("id", clientIds);

      if (profilesError) {
        console.error("Erreur chargement profils clients:", profilesError);
        return;
      }

      if (!isCancelled) setClients(profiles || []);
    }

    loadAccessibleClients();
    return () => {
      isCancelled = true;
    };
  }, [session?.user?.id, canChooseClient]);

  useEffect(() => {
    let isCancelled = false;

    if (selected) {
      setHouses([]);
      return undefined;
    }

    const handle = setTimeout(async () => {
      const term = (q || "").trim();
      if (term.length < 2) {
        if (!isCancelled) setHouses([]);
        return;
      }

      const or = `name.ilike.%${term}%,normalized_name.ilike.%${term}%,city.ilike.%${term}%,country.ilike.%${term}%`;
      const { data, error } = await supabase
        .from("auction_houses")
        .select("id,name,city,country,website,normalized_name")
        .or(or)
        .limit(10);

      if (!isCancelled) {
        if (error) {
          console.error(error);
          setHouses([]);
        } else {
          setHouses(data || []);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      clearTimeout(handle);
    };
  }, [q, selected]);

  useEffect(() => {
    function onDocClick(e) {
      if (!listRef.current) return;
      if (!listRef.current.contains(e.target)) setHouses([]);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

async function runImport() {
  if (!file) return;

  if (!session?.user) {
    setMsg(`❌ ${tr("messages.loginRequired")}`);
    return;
  }

  if (canChooseClient && !importContext.client_id && !isTeamRole(role)) {
    setMsg("❌ Choisissez un client avant d’importer.");
    return;
  }

  setMsg("Import en cours…");
  setImporting(true);

  try {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("user_id", session.user.id);

    if (artworkIdFromUrl) fd.append("artwork_id", artworkIdFromUrl);
    if (artistFromUrl) fd.append("artist", artistFromUrl);

    const { data: json, error } = await supabase.functions.invoke(
      "import_artprice_pdf",
      { body: fd }
    );

    if (error || !json?.ok) {
      throw new Error(error?.message || json?.error || tr("messages.importError"));
    }

    setMsg(`✅ ${tr("messages.processing", { id: json.job_id?.slice(0, 8) })}`);

    // 🔥 trigger OCR backend
try {
  const workerRes = await fetch("https://vision.artkiv.com/process-artprice-jobs?limit=5", {
    method: "POST",
  });

  const workerJson = await workerRes.json();

  if (workerRes.ok && workerJson?.processed > 0) {
    setMsg(`✅ ${tr("messages.processed", { count: workerJson.processed })}`);
  } else {
    setMsg(`✅ ${tr("messages.queued")}`);
  }
} catch {
  setMsg(`✅ ${tr("messages.queued")}`);
}

    setRows([]);

  } catch (error) {
    console.error(error);
    setMsg(`❌ ${error.message}`);
  } finally {
    setImporting(false);
  }
}

  function onSearchKeyDown(e) {
    if (e.key === "Enter" && !selected && houses[0]) {
      e.preventDefault();
      setSelected(houses[0]);
      setHouses([]);
    }
  }

  async function createHouse(e) {
    e?.preventDefault?.();
    if (!addName.trim()) return;
    setSavingAdd(true);

    try {
      const aliasesArray = addAliases
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const { data, error } = await supabase
        .from("auction_houses")
        .insert({
          name: addName.trim(),
          normalized_name: addName.trim().toLowerCase(),
          city: addCity.trim() || null,
          country: addCountry.trim() || null,
          website: addWebsite.trim() || null,
          aliases: aliasesArray.length ? aliasesArray : null,
        })
        .select("id,name,city,country,website,normalized_name")
        .single();

      if (error) {
        console.error(error);
        setMsg("❌ Impossible d’ajouter la maison de ventes.");
        return;
      }

      setSelected(data);
      setShowAdd(false);
      setAddName("");
      setAddCity("");
      setAddCountry("");
      setAddWebsite("");
      setAddAliases("");
      setMsg(`✅ ${tr("messages.houseAdded")}`);
    } finally {
      setSavingAdd(false);
    }
  }

  function clientLabel(client) {
    const name = [client.first_name, client.last_name].filter(Boolean).join(" ");
    return name || client.company_name || client.company || client.id;
  }

  return (
    <div className="text-sm space-y-4">
      <div className="rounded-lg border bg-white p-4">
        <div className="font-semibold text-sm mb-1">{tr("context.title")}</div>
        <div className="text-xs text-gray-500 mb-3">
          {tr("context.description")}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 block mb-1">{tr("context.importedBy")}</label>
            <div className="border rounded px-2 py-1 text-xs bg-gray-50">
              {role} {plan ? `• ${plan}` : ""}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-600 block mb-1">{tr("context.visibility")}</label>
            <div className="border rounded px-2 py-1 text-xs bg-gray-50">
              {importContext.visibility}
            </div>
          </div>
        </div>

        {canChooseClient && !isTeamRole(role) && (
          <div className="mt-3">
            <label className="text-xs text-gray-600 block mb-1">{tr("context.client")} *</label>
            {clients.length > 0 ? (
              <select
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-black focus:outline-none"
                value={selectedClientId}
                onChange={(e) => {
                  setSelectedClientId(e.target.value);
                  setManualClientId("");
                }}
              >
                <option value="">{tr("context.selectClient")}</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {clientLabel(client)}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="w-full border rounded px-2 py-1 text-xs"
                placeholder={tr("context.clientUuid")}
                value={manualClientId}
                onChange={(e) => setManualClientId(e.target.value.trim())}
              />
            )}
            <div className="text-[11px] text-gray-500 mt-1">
              {tr("context.clientWarning")}
            </div>
          </div>
        )}
      </div>

      <div className="mb-3 relative">
        <label className="text-xs text-gray-600 block mb-1">{tr("house.optional")}</label>
        <div className="flex items-center gap-2">
          <input
            className="flex-1 border rounded px-2 py-1 text-xs"
            placeholder={tr("house.search")}
            value={selected ? selected.name : q}
            onChange={(e) => {
              setSelected(null);
              setQ(e.target.value);
            }}
            onKeyDown={onSearchKeyDown}
          />
          {selected && (
            <button
              type="button"
              className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
              onClick={() => {
                setSelected(null);
                setQ("");
              }}
            >
              ✕
            </button>
          )}
          <button
            type="button"
            className="px-2 py-1 text-xs rounded border hover:bg-white"
            onClick={() => setShowAdd(true)}
          >
            + {tr("actions.add")}
          </button>
        </div>

        {!selected && houses.length > 0 && (
          <div ref={listRef} className="border mt-1 rounded bg-white max-h-56 overflow-auto shadow-sm z-10 absolute w-full">
            {houses.map((h) => (
              <div
                key={h.id}
                className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs"
                onClick={() => {
                  setSelected(h);
                  setHouses([]);
                }}
              >
                <div className="font-medium">{h.name}</div>
                <div className="text-xs text-gray-500">{[h.city, h.country].filter(Boolean).join(", ")}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-lg p-5">
            <h2 className="text-lg font-semibold mb-4">{tr("house.addTitle")}</h2>
            <form onSubmit={createHouse} className="space-y-3">
              <div>
                <label className="text-sm text-gray-600">{tr("house.name")} *</label>
                <input className="w-full border rounded px-3 py-2" value={addName} onChange={(e) => setAddName(e.target.value)} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">{tr("house.city")}</label>
                  <input className="w-full border rounded px-3 py-2" value={addCity} onChange={(e) => setAddCity(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm text-gray-600">{tr("house.country")}</label>
                  <input className="w-full border rounded px-3 py-2" value={addCountry} onChange={(e) => setAddCountry(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600">{tr("house.website")}</label>
                <input className="w-full border rounded px-3 py-2" value={addWebsite} onChange={(e) => setAddWebsite(e.target.value)} />
              </div>
              <div>
                <label className="text-sm text-gray-600">{tr("house.aliases")}</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  placeholder="ex: Sotheby's, Sothebys, Sotheby's Paris"
                  value={addAliases}
                  onChange={(e) => setAddAliases(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="px-3 py-2 rounded border" onClick={() => setShowAdd(false)} disabled={savingAdd}>
                  {tr("actions.cancel")}
                </button>
                <button type="submit" className="px-4 py-2 rounded bg-black text-white disabled:opacity-50" disabled={!addName.trim() || savingAdd}>
                  {savingAdd ? tr("actions.saving") : tr("actions.add")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="flex gap-2 items-center mb-3">
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs flex-1" />
<button
  type="button"
  className="px-5 py-2 bg-black text-white text-sm rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 transition"
  onClick={runImport}
  disabled={!file || importing}
>
  {importing ? tr("actions.importing") : tr("actions.import")}
</button>
      </div>

      {msg && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            msg.startsWith("✅")
              ? "border-green-200 bg-green-50 text-green-800"
              : msg.startsWith("❌")
                ? "border-red-200 bg-red-50 text-red-800"
                : "border-gray-200 bg-gray-50 text-gray-700"
          }`}
        >
          <div>{msg}</div>
        </div>
      )}

      {artworkIdFromUrl && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/estimation-semblables/${artworkIdFromUrl}`)}
            className="inline-flex items-center justify-center rounded bg-black px-5 py-2 text-sm font-medium text-white hover:bg-gray-800 transition"
          >
            ← {tr("actions.backToCup")}
          </button>
          {msg?.startsWith("✅") && (
            <span className="text-xs text-gray-500">
              {tr("messages.returnToCup")}
            </span>
          )}
        </div>
      )}

      {!!rows.length && (
        <table className="w-full text-xs border mt-4">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 border text-xs">{tr("table.artist")}</th>
              <th className="p-2 border text-xs">{tr("table.title")}</th>
              <th className="p-2 border text-xs">Dim (cm)</th>
              <th className="p-2 border text-xs">{tr("table.estimate")}</th>
              <th className="p-2 border text-xs">{tr("table.hammer")}</th>
              <th className="p-2 border text-xs">{tr("table.sale")}</th>
              <th className="p-2 border text-xs">{tr("table.owner")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 border">{r.artist || "—"}</td>
                <td className="p-2 border">{r.title || "—"}</td>
                <td className="p-2 border">
                  {r.width_cm ?? "—"}×{r.height_cm ?? "—"}{r.depth_cm ? `×${r.depth_cm}` : ""}
                </td>
                <td className="p-2 border">
                  {r.estimate_low?.toLocaleString(i18n.resolvedLanguage || i18n.language) || "—"}–{r.estimate_high?.toLocaleString(i18n.resolvedLanguage || i18n.language) || "—"} €
                </td>
                <td className="p-2 border">{r.hammer_price ? `${Number(r.hammer_price).toLocaleString(i18n.resolvedLanguage || i18n.language)} €` : "—"}</td>
                <td className="p-2 border">
                  {r.auction_house || "—"}{r.auction_date ? ` • ${new Date(r.auction_date).toLocaleDateString(i18n.resolvedLanguage || i18n.language)}` : ""}
                </td>
                <td className="p-2 border">
                  <div>{r.visibility || "—"}</div>
                  <div className="text-[10px] text-gray-400">{r.client_id || r.owner_id || "global"}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
